import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { searchContacts, mergeContacts } from '../../services/apiContacts';
import { resolveMediaUrl } from '../../lib/utils';

// ─── Step Indicator ──────────────────────────────────────────────────────────
function StepDot({ n, active, done }) {
    return (
        <div className="flex flex-col items-center gap-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                done  ? 'bg-green-500 text-white'
                      : active ? 'bg-primary text-white ring-4 ring-primary/20'
                               : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
            }`}>
                {done ? <span className="material-symbols-outlined text-sm">check</span> : n}
            </div>
        </div>
    );
}

// ─── Channel Pill ────────────────────────────────────────────────────────────
function ChannelPill({ channel }) {
    if (channel === 'instagram') {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-pink-500 to-purple-500 text-white">
                <span className="material-symbols-outlined text-[10px]">photo_camera</span>
                IG
            </span>
        );
    }
    if (channel === 'instagram_comment') {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-pink-500 to-purple-500 text-white">
                <span className="material-symbols-outlined text-[10px]">chat_bubble</span>
                IG · Comentario
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            <span className="material-symbols-outlined text-[10px]">chat</span>
            WA
        </span>
    );
}

// ─── Contact Summary Card ────────────────────────────────────────────────────
function ContactCard({ contact, label, ismaster = false }) {
    const initials = contact.full_name?.substring(0, 2).toUpperCase() || '??';
    return (
        <div className={`flex-1 rounded-xl border-2 p-4 transition-all ${
            ismaster
                ? 'border-primary/40 bg-primary/5 dark:bg-primary/10'
                : 'border-orange-300/40 bg-orange-50/50 dark:bg-orange-900/10'
        }`}>
            <div className={`text-[10px] font-bold uppercase tracking-widest mb-3 ${
                ismaster ? 'text-primary' : 'text-orange-500'
            }`}>{label}</div>
            <div className="flex items-center gap-3 mb-4">
                {contact.avatar ? (
                    <div className="w-12 h-12 rounded-full bg-cover bg-center border-2 border-white dark:border-gray-800 shadow flex-shrink-0"
                         style={{ backgroundImage: `url('${resolveMediaUrl(contact.avatar)}')` }} />
                ) : (
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0 ${
                        ismaster ? 'bg-primary/15 text-primary' : 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
                    }`}>{initials}</div>
                )}
                <div className="min-w-0">
                    <p className="font-bold text-gray-900 dark:text-white text-sm truncate">{contact.full_name || 'Sin nombre'}</p>
                    {contact.company && <p className="text-xs text-gray-500 truncate">{contact.company}</p>}
                </div>
            </div>
            <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm text-gray-400">call</span>
                    <span className="truncate">{contact.phone || '—'}</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm text-gray-400">mail</span>
                    <span className="truncate">{contact.email || '—'}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="material-symbols-outlined text-sm text-gray-400">chat_bubble</span>
                    <span className="text-gray-500">{contact.conversations_count ?? '?'} chats</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-500">{contact.deals_count ?? '?'} negocios</span>
                </div>
                {contact.conversations?.map(c => (
                    <ChannelPill key={c.id} channel={c.channel} />
                ))}
            </div>
        </div>
    );
}

// ─── Name Selector ───────────────────────────────────────────────────────────
function NameSelector({ master, source, chosen, onChoose }) {
    return (
        <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                ¿Con qué nombre quedará el contacto unificado?
            </p>
            <div className="grid grid-cols-2 gap-3">
                {[
                    { name: master.full_name, label: 'Nombre actual', src: 'master' },
                    { name: source.full_name, label: 'Nombre a unificar', src: 'source' },
                ].map(opt => (
                    <button
                        key={opt.src}
                        onClick={() => onChoose(opt.name)}
                        className={`relative p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${
                            chosen === opt.name
                                ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-md'
                                : 'border-gray-200 dark:border-gray-700 hover:border-primary/40 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                    >
                        {chosen === opt.name && (
                            <span className="absolute top-2 right-2 material-symbols-outlined text-primary text-base">check_circle</span>
                        )}
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{opt.label}</p>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm leading-snug pr-5">{opt.name || '—'}</p>
                    </button>
                ))}
            </div>
            {/* Custom name input */}
            <div className="mt-3">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">O escribe un nombre personalizado</p>
                <input
                    type="text"
                    value={chosen}
                    onChange={e => onChoose(e.target.value)}
                    placeholder="Nombre personalizado..."
                    className={`w-full rounded-lg border text-sm px-3 py-2 transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                        chosen !== master.full_name && chosen !== source.full_name
                            ? 'border-primary'
                            : 'border-gray-200 dark:border-gray-700'
                    }`}
                />
            </div>
        </div>
    );
}

// ─── Main Modal ──────────────────────────────────────────────────────────────
export default function MergeContactModal({ master, isOpen, onClose, onSuccess }) {
    const queryClient = useQueryClient();
    const [step, setStep] = useState(1); // 1=search, 2=review, 3=done
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedSource, setSelectedSource] = useState(null);
    const [chosenName, setChosenName] = useState('');
    const searchRef = useRef(null);
    const debounceRef = useRef(null);

    // Reset on open/close
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSearchQuery('');
            setSearchResults([]);
            setSelectedSource(null);
            setChosenName(master?.full_name || '');
            setTimeout(() => searchRef.current?.focus(), 100);
        }
    }, [isOpen, master]);

    // Debounced search
    const handleSearch = useCallback((q) => {
        setSearchQuery(q);
        clearTimeout(debounceRef.current);
        if (!q.trim() || q.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const results = await searchContacts(q);
                // Exclude master from results
                setSearchResults(results.filter(c => c.id !== master.id));
            } catch (_) {
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 350);
    }, [master]);

    const handleSelectSource = (contact) => {
        setSelectedSource(contact);
        setChosenName(master.full_name || '');
        setStep(2);
    };

    const mergeMutation = useMutation({
        mutationFn: () => mergeContacts(master.id, selectedSource.id, chosenName),
        onSuccess: (data) => {
            setStep(3);
            queryClient.invalidateQueries({ queryKey: ['contact', String(master.id)] });
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            setTimeout(() => {
                onSuccess?.(data.contact);
                onClose();
            }, 1800);
        },
    });

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Panel */}
            <div className="relative z-10 w-full max-w-2xl bg-white dark:bg-[#1a100c] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 flex flex-col max-h-[90vh] overflow-hidden">

                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className="material-symbols-outlined text-primary text-lg">merge</span>
                            Unificar Contacto
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Unificando: <strong className="text-gray-700 dark:text-gray-300">{master?.full_name}</strong>
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                        <span className="material-symbols-outlined text-xl">close</span>
                    </button>
                </div>

                {/* Step Progress */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <StepDot n={1} active={step === 1} done={step > 1} />
                        <div className={`flex-1 h-0.5 rounded transition-all duration-300 ${step > 1 ? 'bg-green-400' : 'bg-gray-200 dark:bg-gray-700'}`} />
                        <StepDot n={2} active={step === 2} done={step > 2} />
                        <div className={`flex-1 h-0.5 rounded transition-all duration-300 ${step > 2 ? 'bg-green-400' : 'bg-gray-200 dark:bg-gray-700'}`} />
                        <StepDot n={3} active={step === 3} done={step === 3} />
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 mt-1.5">
                        <span className={step === 1 ? 'text-primary font-semibold' : ''}>Buscar</span>
                        <span className={step === 2 ? 'text-primary font-semibold' : ''}>Revisar</span>
                        <span className={step === 3 ? 'text-green-500 font-semibold' : ''}>Listo</span>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto no-scrollbar p-6">

                    {/* ── STEP 1: Search ──────────────────────────────── */}
                    {step === 1 && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Busca el contacto duplicado que quieres unificar con <strong className="text-gray-900 dark:text-white">{master?.full_name}</strong>. Todo su historial (chats, notas, negocios) quedará en el contacto principal.
                            </p>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 text-lg">search</span>
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => handleSearch(e.target.value)}
                                    placeholder="Buscar por nombre, teléfono o email..."
                                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                                />
                                {isSearching && (
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-primary text-lg animate-spin">sync</span>
                                )}
                            </div>

                            {/* Results */}
                            {searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                                <div className="text-center py-8 text-gray-400">
                                    <span className="material-symbols-outlined text-4xl block mb-2">person_search</span>
                                    <p className="text-sm">Sin resultados para "{searchQuery}"</p>
                                </div>
                            )}
                            {searchQuery.length < 2 && (
                                <div className="text-center py-8 text-gray-300 dark:text-gray-700">
                                    <span className="material-symbols-outlined text-5xl block mb-2">manage_accounts</span>
                                    <p className="text-sm text-gray-400">Escribe al menos 2 caracteres para buscar</p>
                                </div>
                            )}

                            <div className="divide-y divide-gray-100 dark:divide-gray-800 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
                                {searchResults.map(contact => {
                                    const init = contact.full_name?.substring(0, 2).toUpperCase() || '??';
                                    return (
                                        <button
                                            key={contact.id}
                                            onClick={() => handleSelectSource(contact)}
                                            className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors text-left group"
                                        >
                                            {contact.avatar ? (
                                                <div className="w-10 h-10 rounded-full bg-cover bg-center flex-shrink-0" style={{ backgroundImage: `url('${resolveMediaUrl(contact.avatar)}')` }} />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-sm font-bold text-gray-500 flex-shrink-0 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                                                    {init}
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{contact.full_name || 'Sin nombre'}</p>
                                                <p className="text-xs text-gray-400 truncate">{contact.phone || contact.email || contact.company || '—'}</p>
                                            </div>
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                {contact.conversations?.length > 0 && contact.conversations.slice(0, 2).map((c, i) => (
                                                    <ChannelPill key={i} channel={c.channel} />
                                                ))}
                                                <span className="material-symbols-outlined text-gray-300 group-hover:text-primary transition-colors text-base">chevron_right</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── STEP 2: Review & Configure ──────────────────── */}
                    {step === 2 && selectedSource && (
                        <div className="space-y-5">
                            {/* Side-by-side comparison */}
                            <div className="flex gap-3">
                                <ContactCard contact={master} label="Contacto Principal" ismaster />
                                <div className="flex items-center flex-shrink-0">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-primary text-base">merge</span>
                                    </div>
                                </div>
                                <ContactCard contact={selectedSource} label="Se unificará en" />
                            </div>

                            {/* Divider */}
                            <div className="border-t border-gray-100 dark:border-gray-800" />

                            {/* Name selector */}
                            <NameSelector
                                master={master}
                                source={selectedSource}
                                chosen={chosenName}
                                onChoose={setChosenName}
                            />

                            {/* Divider */}
                            <div className="border-t border-gray-100 dark:border-gray-800" />

                            {/* What will happen */}
                            <div className="bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-700/40 rounded-xl p-4 space-y-2">
                                <div className="flex items-start gap-2">
                                    <span className="material-symbols-outlined text-amber-500 text-base flex-shrink-0 mt-0.5">info</span>
                                    <div className="text-xs text-amber-800 dark:text-amber-300 space-y-1">
                                        <p className="font-semibold">¿Qué pasará al unificar?</p>
                                        <ul className="list-disc list-inside space-y-0.5 text-amber-700 dark:text-amber-400">
                                            <li>Todas las conversaciones, notas y negocios de <strong>{selectedSource.full_name}</strong> pasarán a <strong>{chosenName || master.full_name}</strong>.</li>
                                            <li>Los campos vacíos del contacto principal se completarán con los datos del otro.</li>
                                            <li><strong>{selectedSource.full_name}</strong> quedará <strong>archivado</strong> (no eliminado) y desaparecerá de la lista de contactos.</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {mergeMutation.isError && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 rounded-xl p-4 text-xs text-red-700 dark:text-red-400 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-base">error</span>
                                    {mergeMutation.error?.response?.data?.message || 'Ocurrió un error al unificar. Intenta de nuevo.'}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── STEP 3: Success ──────────────────────────────── */}
                    {step === 3 && (
                        <div className="flex flex-col items-center justify-center py-12 gap-4">
                            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <span className="material-symbols-outlined text-green-500 text-3xl">check_circle</span>
                            </div>
                            <div className="text-center">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">¡Contactos unificados!</h3>
                                <p className="text-sm text-gray-500">Ahora todo el historial está en <strong className="text-gray-700 dark:text-gray-300">{chosenName}</strong>.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {step !== 3 && (
                    <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0 bg-gray-50/50 dark:bg-gray-900/30">
                        <button
                            onClick={() => step === 2 ? setStep(1) : onClose()}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                            {step === 2 ? (
                                <span className="flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-base">arrow_back</span>
                                    Volver
                                </span>
                            ) : 'Cancelar'}
                        </button>

                        {step === 2 && (
                            <button
                                onClick={() => mergeMutation.mutate()}
                                disabled={!chosenName.trim() || mergeMutation.isPending}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold transition-all shadow-sm hover:shadow-md"
                            >
                                {mergeMutation.isPending ? (
                                    <span className="material-symbols-outlined text-base animate-spin">sync</span>
                                ) : (
                                    <span className="material-symbols-outlined text-base">merge</span>
                                )}
                                {mergeMutation.isPending ? 'Unificando...' : 'Confirmar Unificación'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
